import classNames from "classnames";
import React, { useState, useRef, useLayoutEffect, useCallback, HTMLAttributes } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export type TruncatedTextProps = HTMLAttributes<HTMLDivElement>;

/**
 * A component that renders a div with a single line of possibly truncated text. If the text is truncated, the full
 * text will be shown in a tooltip on hover.
 *
 * This component applies the bootstrap text-truncate class, which prevent text-wrapping, and will truncate the text with
 * an ellipsis.
 *
 * The consumer of this component is responsible for giving this element a fixed width that can cause it to overflow.
 * For example, using a fixed value for the `width` or `max-width` CSS properties will achieve this.
 */
export const TruncatedText: React.FC<TruncatedTextProps> = (props) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Callback function to update the overflowing state
  const checkOverflow = useCallback(() => {
    if (!textRef.current) return;
    setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
  }, []);

  // Effect callback to check overflow on initial render and resize
  useLayoutEffect(() => {
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [checkOverflow, props.children]);

  return (
    <OverlayTrigger overlay={isOverflowing ? <Tooltip id="text-tooltip">{props.children}</Tooltip> : <></>}>
      <div ref={textRef} {...props} className={classNames("text-truncate", props.className)} />
    </OverlayTrigger>
  );
};
